import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Campaign } from '../types';

interface QueueStatus {
  pending: number;
  failed: number;
  worker_status: 'active' | 'idle' | 'stopped';
  oldest_pending_minutes: number | null;
  last_failed_at: string | null;
}

// Inline SVG Icons (compatible with React 19)
const Icons = {
  Users: ({ size = 24 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Mail: ({ size = 24 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Send: ({ size = 24 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  TrendingUp: ({ size = 24 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  TrendingDown: ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  ArrowUpRight: ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" />
    </svg>
  ),
  Plus: ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  FileText: ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14,2 14,8 20,8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  Zap: ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  ChevronRight: ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  MoreHorizontal: ({ size = 18 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
    </svg>
  ),
  CheckCircle2: ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="9 12 12 15 16 10" />
    </svg>
  ),
  Clock: ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  AlertCircle: ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
};

// Premium Loading Skeleton
const StatsSkeleton: React.FC = () => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>
    {[1, 2, 3, 4].map((i) => (
      <div
        key={i}
        style={{
          padding: '24px',
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
            backgroundSize: '200% 100%',
            borderRadius: '12px',
            marginBottom: '16px',
            animation: 'shimmer 1.5s infinite',
          }}
        />
        <div
          style={{
            height: '16px',
            background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
            backgroundSize: '200% 100%',
            borderRadius: '4px',
            width: '60%',
            marginBottom: '12px',
            animation: 'shimmer 1.5s infinite',
          }}
        />
        <div
          style={{
            height: '32px',
            background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
            backgroundSize: '200% 100%',
            borderRadius: '4px',
            width: '40%',
            animation: 'shimmer 1.5s infinite',
          }}
        />
      </div>
    ))}
  </div>
);

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: number;
  trend: number;
  trendLabel: string;
  link: string;
  linkText: string;
  delay: number;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  iconBg,
  iconColor,
  label,
  value,
  trend,
  trendLabel,
  link,
  linkText,
  delay,
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const duration = 800;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);

  const trendPositive = trend >= 0;

  return (
    <div
      style={{
        padding: '24px',
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: isHovered
          ? '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)'
          : '0 1px 3px 0 rgba(0,0,0,0.1)',
        transition: 'all 200ms ease-out',
        cursor: 'pointer',
        animation: `fadeInUp 400ms ease-out ${delay}ms forwards`,
        opacity: 0,
        transform: 'translateY(10px)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            background: iconBg,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 200ms ease-out',
          }}
        >
          {icon}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 10px',
            background: trendPositive ? '#ecfdf5' : '#fef2f2',
            borderRadius: '20px',
            color: trendPositive ? '#059669' : '#dc2626',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          {trendPositive ? <Icons.TrendingUp size={14} /> : <Icons.TrendingDown size={14} />}
          <span>{Math.abs(trend)}%</span>
        </div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </span>
      </div>

      <div
        style={{
          fontSize: '2rem',
          fontWeight: 700,
          color: '#0f172a',
          marginBottom: '16px',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {displayValue.toLocaleString()}
      </div>

      <Link
        to={link}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#6366f1',
          textDecoration: 'none',
          transition: 'gap 150ms ease-out',
        }}
      >
        {linkText}
        <span style={{ transform: isHovered ? 'translate(2px, -2px)' : 'none', transition: 'transform 150ms ease-out' }}>
          <Icons.ArrowUpRight size={16} />
        </span>
      </Link>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalContacts: 0,
    totalCampaigns: 0,
    totalSent: 0,
    avgOpenRate: 0,
    recentCampaigns: [] as Campaign[],
    trends: {
      contacts: 0,
      campaigns: 0,
      sent: 0,
      openRate: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Queue status — separate state so polls never re-render the whole page
  const [queue, setQueue] = useState<QueueStatus | null>(null);
  const queueIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchQueueStatus = async () => {
    try {
      const res = await api.get('/queue/status');
      setQueue(res.data);
    } catch {
      // silently ignore — queue widget just stays as-is
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchQueueStatus();
    // Poll every 30 s silently — no loading state, no blink
    queueIntervalRef.current = setInterval(fetchQueueStatus, 30000);
    return () => {
      if (queueIntervalRef.current) clearInterval(queueIntervalRef.current);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard');
      
      setStats({
        totalContacts: response.data.totalContacts || 0,
        totalCampaigns: response.data.totalCampaigns || 0,
        totalSent: response.data.totalSent || 0,
        avgOpenRate: response.data.avgOpenRate || 0,
        recentCampaigns: response.data.recentCampaigns || [],
        trends: response.data.trends || {
          contacts: 0,
          campaigns: 0,
          sent: 0,
          openRate: 0,
        },
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
      alert('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return { bg: '#ecfdf5', color: '#059669', icon: <Icons.CheckCircle2 size={14} />, label: 'Sent' };
      case 'sending':
        return { bg: '#fef3c7', color: '#d97706', icon: <Icons.Clock size={14} />, label: 'Sending' };
      case 'draft':
        return { bg: '#f1f5f9', color: '#64748b', icon: <Icons.FileText size={14} />, label: 'Draft' };
      case 'failed':
        return { bg: '#fef2f2', color: '#dc2626', icon: <Icons.AlertCircle size={14} />, label: 'Failed' };
      default:
        return { bg: '#eff6ff', color: '#2563eb', icon: <Icons.Clock size={14} />, label: 'Draft' };
    }
  };

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  if (loading) {
    return (
      <div style={{ padding: '32px', animation: 'fadeInUp 300ms ease-out' }}>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <StatsSkeleton />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: '1440px', margin: '0 auto', animation: 'fadeInUp 300ms ease-out' }}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .main-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 640px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* Header Section */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '4px' }}>{today}</p>
            <h1
              style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#0f172a',
                margin: 0,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0] || 'there'}
            </h1>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'white',
                color: '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: refreshing ? 'not-allowed' : 'pointer',
                transition: 'all 150ms ease-out',
                opacity: refreshing ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!refreshing) {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }
              }}
              onMouseLeave={(e) => {
                if (!refreshing) {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
            >
              <Icons.Clock size={18} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <Link
              to="/campaigns"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                borderRadius: '10px',
                fontSize: '0.875rem',
                fontWeight: 600,
                textDecoration: 'none',
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                transition: 'all 150ms ease-out',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.35)';
              }}
            >
              <Icons.Plus size={18} />
              New Campaign
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '24px', 
        marginBottom: '32px'
      }} className="stats-grid">
        <StatCard
          icon={<Icons.Users size={24} />}
          iconBg="#eef2ff"
          iconColor="#6366f1"
          label="Total Contacts"
          value={stats.totalContacts}
          trend={stats.trends.contacts}
          trendLabel="vs last week"
          link="/contacts"
          linkText="View contacts"
          delay={0}
        />
        <StatCard
          icon={<Icons.Mail size={24} />}
          iconBg="#ecfdf5"
          iconColor="#10b981"
          label="Campaigns"
          value={stats.totalCampaigns}
          trend={stats.trends.campaigns}
          trendLabel="vs last week"
          link="/campaigns"
          linkText="View campaigns"
          delay={100}
        />
        <StatCard
          icon={<Icons.Send size={24} />}
          iconBg="#f0f9ff"
          iconColor="#0ea5e9"
          label="Emails Sent"
          value={stats.totalSent}
          trend={stats.trends.sent}
          trendLabel="vs last week"
          link="/campaigns"
          linkText="View analytics"
          delay={200}
        />
        <StatCard
          icon={<Icons.TrendingUp size={24} />}
          iconBg="#fef3c7"
          iconColor="#f59e0b"
          label="Avg Open Rate"
          value={Math.round(stats.avgOpenRate)}
          trend={stats.trends.openRate}
          trendLabel="vs last month"
          link="/campaigns"
          linkText="View details"
          delay={300}
        />
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '32px' }} className="main-grid">
        {/* Left Column */}
        <div>
          {/* Recent Campaigns */}
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#0f172a',
                  margin: 0,
                }}
              >
                Recent Campaigns
              </h2>
              <Link
                to="/campaigns"
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#6366f1',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                View all
                <Icons.ChevronRight size={16} />
              </Link>
            </div>

            {stats.recentCampaigns.length === 0 ? (
              /* Premium Empty State */
              <div style={{ padding: '64px 48px', textAlign: 'center' }}>
                {/* Illustration */}
                <div
                  style={{
                    width: '120px',
                    height: '120px',
                    margin: '0 auto 24px',
                    background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                    borderRadius: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <span style={{ color: '#6366f1' }}><Icons.Mail size={48} /></span>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '-8px',
                      right: '-8px',
                      width: '40px',
                      height: '40px',
                      background: 'white',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                  >
                    <span style={{ color: '#6366f1' }}><Icons.Plus size={20} /></span>
                  </div>
                </div>

                <h3
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    marginBottom: '8px',
                  }}
                >
                  Ready to send your first campaign?
                </h3>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    marginBottom: '32px',
                    maxWidth: '360px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    lineHeight: 1.6,
                  }}
                >
                  Create personalized email campaigns to engage your audience and drive results
                </p>

                <Link
                  to="/campaigns"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                    transition: 'all 150ms ease-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.45)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.35)';
                  }}
                >
                  <Icons.Zap size={18} />
                  Create Your First Campaign
                </Link>

                {/* Tips Section */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                    marginTop: '48px',
                    maxWidth: '600px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  {[
                    {
                      icon: <Icons.Users size={20} />,
                      title: 'Import Contacts',
                      desc: 'Upload your subscriber list',
                      color: '#6366f1',
                      bg: '#eef2ff',
                    },
                    {
                      icon: <Icons.FileText size={20} />,
                      title: 'Choose Template',
                      desc: 'Pick from ready-made designs',
                      color: '#10b981',
                      bg: '#ecfdf5',
                    },
                    {
                      icon: <Icons.Send size={20} />,
                      title: 'Send & Track',
                      desc: 'Monitor performance in real-time',
                      color: '#0ea5e9',
                      bg: '#f0f9ff',
                    },
                  ].map((tip, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '16px',
                        background: tip.bg,
                        borderRadius: '12px',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          background: 'white',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: tip.color,
                          marginBottom: '12px',
                        }}
                      >
                        {tip.icon}
                      </div>
                      <h4
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#0f172a',
                          marginBottom: '4px',
                        }}
                      >
                        {tip.title}
                      </h4>
                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: '#64748b',
                          margin: 0,
                          lineHeight: 1.4,
                        }}
                      >
                        {tip.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th
                        style={{
                          padding: '14px 24px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Campaign
                      </th>
                      <th
                        style={{
                          padding: '14px 24px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: '14px 24px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Recipients
                      </th>
                      <th
                        style={{
                          padding: '14px 24px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Performance
                      </th>
                      <th style={{ padding: '14px 24px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentCampaigns.map((campaign, idx) => {
                      const status = getStatusConfig(campaign.status);
                      // Calculate open rate from campaign data
                      const sentCount = campaign.sent_count || 0;
                      const openRate = sentCount > 0 && campaign.opened_count 
                        ? Math.round((campaign.opened_count / sentCount) * 100)
                        : 0;
                      return (
                        <tr
                          key={campaign.id}
                          style={{
                            borderTop: '1px solid #e2e8f0',
                            background: idx % 2 === 0 ? 'white' : '#fafafa',
                            transition: 'background 150ms ease-out',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              idx % 2 === 0 ? 'white' : '#fafafa';
                          }}
                        >
                          <td style={{ padding: '16px 24px' }}>
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#0f172a',
                                  marginBottom: '2px',
                                }}
                              >
                                {campaign.name}
                              </div>
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#94a3b8',
                                }}
                              >
                                Created {new Date(campaign.created_at || '').toLocaleDateString()}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                borderRadius: '9999px',
                                background: status.bg,
                                color: status.color,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                              }}
                            >
                              {status.icon}
                              {status.label}
                            </span>
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>
                              {campaign.total_recipients.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              {campaign.sent_count.toLocaleString()} sent
                            </div>
                          </td>
                          <td style={{ padding: '16px 24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: 1, maxWidth: '100px' }}>
                                <div
                                  style={{
                                    height: '6px',
                                    background: '#e2e8f0',
                                    borderRadius: '9999px',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <div
                                    style={{
                                      height: '100%',
                                      width: `${openRate}%`,
                                      background: '#10b981',
                                      borderRadius: '9999px',
                                    }}
                                  />
                                </div>
                              </div>
                              <span
                                style={{
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: '#10b981',
                                }}
                              >
                                {openRate}%
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                              Open rate
                            </div>
                          </td>
                          <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                            <button
                              style={{
                                padding: '8px',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                color: '#94a3b8',
                                transition: 'all 150ms ease-out',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f1f5f9';
                                e.currentTarget.style.color = '#64748b';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#94a3b8';
                              }}
                            >
                              <Icons.MoreHorizontal size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Activity Feed */}
        <div>
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
            }}
          >
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#0f172a',
                margin: '0 0 20px 0',
              }}
            >
              Recent Activity
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                {
                  icon: <Icons.Mail size={16} />,
                  iconBg: '#eef2ff',
                  iconColor: '#6366f1',
                  title: 'Campaign sent',
                  desc: 'Summer Sale campaign sent to 1,240 contacts',
                  time: '2 min ago',
                },
                {
                  icon: <Icons.Users size={16} />,
                  iconBg: '#ecfdf5',
                  iconColor: '#10b981',
                  title: 'New subscriber',
                  desc: 'john@example.com joined your newsletter',
                  time: '1 hour ago',
                },
                {
                  icon: <Icons.FileText size={16} />,
                  iconBg: '#f0f9ff',
                  iconColor: '#0ea5e9',
                  title: 'Template updated',
                  desc: 'Welcome Email template was modified',
                  time: '3 hours ago',
                },
              ].map((activity, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '12px',
                    transition: 'background 150ms ease-out',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      background: activity.iconBg,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: activity.iconColor,
                      flexShrink: 0,
                    }}
                  >
                    {activity.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#0f172a',
                        marginBottom: '2px',
                      }}
                    >
                      {activity.title}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#64748b',
                        marginBottom: '4px',
                        lineHeight: 1.4,
                      }}
                    >
                      {activity.desc}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#94a3b8',
                      }}
                    >
                      {activity.time}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              style={{
                width: '100%',
                marginTop: '20px',
                padding: '10px',
                background: 'transparent',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#64748b',
                cursor: 'pointer',
                transition: 'all 150ms ease-out',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8fafc';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              View all activity
            </button>
          </div>

          {/* Queue Status Widget */}
          {queue !== null && (
            <div
              style={{
                marginTop: '24px',
                background: 'white',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                padding: '20px 24px',
                boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)',
              }}
            >
              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                  Queue Status
                </h2>
                {/* Status pill */}
                {queue.worker_status === 'active' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '9999px', background: '#ecfdf5', color: '#059669', fontSize: '0.75rem', fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'pulse 2s infinite' }} />
                    Active
                  </span>
                )}
                {queue.worker_status === 'idle' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '9999px', background: '#f1f5f9', color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#94a3b8', display: 'inline-block' }} />
                    Idle
                  </span>
                )}
                {queue.worker_status === 'stopped' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '9999px', background: '#fef2f2', color: '#dc2626', fontSize: '0.75rem', fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                    Worker Stopped
                  </span>
                )}
              </div>

              {/* Stats row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ padding: '12px 14px', background: queue.pending > 0 ? '#fef3c7' : '#f8fafc', borderRadius: '10px', border: `1px solid ${queue.pending > 0 ? '#fcd34d' : '#e2e8f0'}` }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: queue.pending > 0 ? '#92400e' : '#0f172a' }}>{queue.pending}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>Pending jobs</div>
                </div>
                <div style={{ padding: '12px 14px', background: queue.failed > 0 ? '#fef2f2' : '#f8fafc', borderRadius: '10px', border: `1px solid ${queue.failed > 0 ? '#fca5a5' : '#e2e8f0'}` }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: queue.failed > 0 ? '#dc2626' : '#0f172a' }}>{queue.failed}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>Failed jobs</div>
                </div>
              </div>

              {/* Worker stopped warning */}
              {queue.worker_status === 'stopped' && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#dc2626', lineHeight: 1.5 }}>
                    Jobs have been waiting {queue.oldest_pending_minutes} min. Run: <code style={{ background: '#fee2e2', padding: '1px 5px', borderRadius: '4px' }}>php artisan queue:work</code>
                  </p>
                </div>
              )}

              {/* Link to settings */}
              {queue.failed > 0 && (
                <Link
                  to="/settings"
                  style={{ display: 'block', marginTop: '12px', textAlign: 'center', fontSize: '0.75rem', color: '#6366f1', fontWeight: 500, textDecoration: 'none' }}
                >
                  Manage failed jobs in Settings →
                </Link>
              )}

              <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
